import { memo } from 'react'

import { Text } from '@cash-me/react-components'

import { Polygon } from 'components/polygon'
import useWindowSize from 'hooks/useWindowSize'
import { theme } from 'styles/theme'
import { formatCurrencyAbbreviated } from 'utils/formatCurrencyAbbreviated'

import { FlaskProps } from './flask.model'
import * as S from './styles'
import { FLASK_HANDLE_ATTR, useFlask } from './useFlask'

const CAPTION_ID = 'ideal-credit-flask-caption'

const NEED_LABEL = 'Quanto você precisa?'

const MIN_VISUAL_PCT = 0
const MAX_VISUAL_PCT = 100
const MOBILE_MIN_VISUAL_PCT = 40
const MOBILE_MAX_VISUAL_PCT = 86

export const Flask = memo(({ value, scale, onChange, onCommit, estimate }: FlaskProps) => {
  const { isMobile } = useWindowSize()

  const showMobileReadout = isMobile && Boolean(estimate)

  const minVisualPct = showMobileReadout ? MOBILE_MIN_VISUAL_PCT : MIN_VISUAL_PCT
  const maxVisualPct = showMobileReadout ? MOBILE_MAX_VISUAL_PCT : MAX_VISUAL_PCT

  const { trackRef, isDragging, visualPct, onPointerDown, onKeyDown, onBlur } = useFlask({
    value,
    scale,
    onChange,
    onCommit,
    minVisualPct,
    maxVisualPct,
    handleOnly: isMobile
  })

  const formattedValue = formatCurrencyAbbreviated(value)

  // Mobile labels the slider directly (Caption is not rendered); desktop points to the Caption.
  const labelProps = showMobileReadout ? { 'aria-label': NEED_LABEL } : { 'aria-labelledby': CAPTION_ID }

  return (
    <S.Root>
      <S.Vessel
        ref={trackRef}
        $isDragging={isDragging}
        role="slider"
        tabIndex={0}
        aria-orientation="vertical"
        aria-valuemin={scale.min}
        aria-valuemax={scale.max}
        aria-valuenow={value}
        aria-valuetext={formattedValue}
        onPointerDown={onPointerDown}
        onKeyDown={onKeyDown}
        onBlur={onBlur}
        {...labelProps}
      >
        {!isMobile && <S.Ticks aria-hidden />}

        <S.Water $isDragging={isDragging} style={{ transform: `translateY(${100 - visualPct}%)` }}>
          <S.WaveNeon aria-hidden>
            <svg viewBox="0 0 240 28" preserveAspectRatio="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M0 14 C 30 6 90 22 120 14 C 150 6 210 22 240 14 L240 25 L0 20 Z"
                fill={theme.colors['cashme-blue-neon']}
              />
            </svg>
          </S.WaveNeon>

          <S.WaveBlue aria-hidden>
            <svg viewBox="0 0 240 28" preserveAspectRatio="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M0 14 C 30 6 90 22 120 14 C 150 6 210 22 240 14 L240 28 L0 28 Z"
                fill={theme.colors['blue-background-strong-default']}
              />
            </svg>
          </S.WaveBlue>

          <S.Grip aria-hidden $alignRight={showMobileReadout} $interactive={isMobile} {...{ [FLASK_HANDLE_ATTR]: '' }}>
            <Polygon type="ChevronUpDown" color="#ffffff" />
          </S.Grip>
        </S.Water>

        {!showMobileReadout && (
          <S.Value>
            <Text as="span" size="heading3" weight="medium" color="var(--colors-neutral-text-contrast-default)">
              {formattedValue}
            </Text>
          </S.Value>
        )}

        {showMobileReadout && estimate && (
          <S.WaterEstimate>
            <Text as="span" size="body1" weight="regular" color="var(--colors-neutral-text-contrast-default)">
              {estimate.label}
            </Text>
            <Text as="span" size="heading4" weight="bold" color="var(--colors-neutral-text-contrast-default)">
              {estimate.value}
            </Text>
          </S.WaterEstimate>
        )}

        {showMobileReadout && (
          <S.NeedBox $isDragging={isDragging} style={{ top: `calc(24rem * ${100 - visualPct} / 100)` }}>
            <Text as="span" size="body2" weight="regular" color="var(--colors-neutral-text-contrast-default)">
              {NEED_LABEL}
            </Text>
            <Text as="span" size="heading5" weight="bold" color="var(--colors-neutral-text-contrast-default)">
              {formattedValue}
            </Text>
          </S.NeedBox>
        )}
      </S.Vessel>

      {!showMobileReadout && (
        <S.Caption id={CAPTION_ID}>
          <Text as="span" size="body1" weight="regular" color="var(--colors-neutral-text-contrast-default)">
            {NEED_LABEL}
          </Text>
        </S.Caption>
      )}
    </S.Root>
  )
})

Flask.displayName = 'Flask'
